import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../services/api';
import { getCurrencySymbol } from '../utils/currency';
import DataTable from '../components/DataTable';
import IconButton from '../components/IconButton';
import './ServiceTemplates.css';
import './Repairs.css';

function ServiceTemplates({ tenantId }) {
  const navigate = useNavigate();
  const [templates, setTemplates] = useState([]);
  const [tenant, setTenant] = useState(null);
  const [hourlyRates, setHourlyRates] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    materials: [{ description: '', amount: 0, quantity: 1 }],
    labor: [{ hours: 0, hourlyRateId: '', description: '' }]
  });

  useEffect(() => {
    if (tenantId) {
      loadTenant();
      loadTemplates();
      loadHourlyRates();
    }
  }, [tenantId]);

  const loadTenant = async () => {
    try {
      const data = await api.get('/tenants', null);
      const currentTenant = data.find(t => t.id === tenantId) || data[0];
      setTenant(currentTenant);
    } catch (error) {
      console.error('Error loading tenant:', error);
    }
  };

  const currency = getCurrencySymbol(tenant?.taxSystem || 'DE');

  const loadTemplates = async () => {
    try {
      const data = await api.get('/service-templates', tenantId);
      setTemplates(data);
    } catch (error) {
      console.error('Error loading templates:', error);
    }
  };

  const loadHourlyRates = async () => {
    try {
      const data = await api.get('/hourly-rates', tenantId);
      setHourlyRates(data);
    } catch (error) {
      console.error('Error loading hourly rates:', error);
    }
  };

  const calculateLaborAmount = (lab) => {
    if (lab.hours && lab.hourlyRateId) {
      const rate = hourlyRates.find(r => r.id === lab.hourlyRateId);
      if (rate) {
        return lab.hours * rate.rate;
      }
    }
    return 0;
  };

  const handleMaterialChange = (index, field, value) => {
    const newMaterials = [...formData.materials];
    if (field === 'amount' || field === 'quantity') {
      newMaterials[index] = { ...newMaterials[index], [field]: value === '' ? '' : value };
    } else {
      newMaterials[index] = { ...newMaterials[index], [field]: value };
    }
    setFormData({ ...formData, materials: newMaterials });
  };

  const handleMaterialBlur = (index, field) => {
    const newMaterials = [...formData.materials];
    if (field === 'amount' || field === 'quantity') {
      const val = newMaterials[index][field] === '' ? 0 : parseFloat(newMaterials[index][field]) || 0;
      newMaterials[index] = { ...newMaterials[index], [field]: val };
      setFormData({ ...formData, materials: newMaterials });
    }
  };

  const handleLaborChange = (index, field, value) => {
    const newLabor = [...formData.labor];
    if (field === 'hours') {
      newLabor[index] = { ...newLabor[index], [field]: value === '' ? '' : value };
    } else {
      newLabor[index] = { ...newLabor[index], [field]: value };
    }
    setFormData({ ...formData, labor: newLabor });
  };

  const handleLaborBlur = (index, field) => {
    const newLabor = [...formData.labor];
    if (field === 'hours') {
      const val = newLabor[index][field] === '' ? 0 : parseFloat(newLabor[index][field]) || 0;
      newLabor[index] = { ...newLabor[index], [field]: val };
      setFormData({ ...formData, labor: newLabor });
    }
  };

  const addMaterial = () => {
    const currentMaterials = formData.materials.filter(mat => 
      mat.description && mat.description.trim() !== ''
    );
    setFormData({
      ...formData,
      materials: [...currentMaterials, { description: '', amount: 0, quantity: 1 }]
    });
  };

  const removeMaterial = (index) => {
    const newMaterials = formData.materials.filter((_, i) => i !== index);
    setFormData({ ...formData, materials: newMaterials });
  };

  const addLabor = () => {
    setFormData({
      ...formData,
      labor: [...formData.labor, { hours: 0, hourlyRateId: '', description: '' }]
    });
  };

  const removeLabor = (index) => {
    const newLabor = formData.labor.filter((_, i) => i !== index);
    setFormData({ ...formData, labor: newLabor });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const submitData = {
        ...formData,
        materials: formData.materials.map(mat => ({
          description: mat.description || '',
          amount: mat.amount === '' ? 0 : parseFloat(mat.amount) || 0,
          quantity: mat.quantity === '' ? 1 : parseFloat(mat.quantity) || 1
        })),
        labor: formData.labor.map(lab => ({
          hours: lab.hours === '' ? 0 : parseFloat(lab.hours) || 0,
          hourlyRateId: lab.hourlyRateId || '',
          description: lab.description || ''
        }))
      };
      
      if (editingTemplate) {
        await api.put(`/service-templates/${editingTemplate.id}`, submitData, tenantId);
      } else {
        await api.post('/service-templates', submitData, tenantId);
      }
      setShowForm(false);
      setEditingTemplate(null);
      setFormData({
        name: '',
        description: '',
        materials: [{ description: '', amount: 0, quantity: 1 }],
        labor: [{ hours: 0, hourlyRateId: '', description: '' }]
      });
      loadTemplates();
    } catch (error) {
      console.error('Error saving template:', error);
    }
  };

  const handleEdit = (template) => {
    setEditingTemplate(template);
    let materials = template.materials || [];
    let labor = template.labor || [];
    
    if (template.items && Array.isArray(template.items) && template.items.length > 0 && (!materials.length && !labor.length)) {
      materials = template.items.filter(item => !item.hours && !item.hourlyRateId).map(item => ({
        description: item.description || '',
        amount: item.amount || 0,
        quantity: item.quantity || 1
      }));
      labor = template.items.filter(item => item.hours || item.hourlyRateId).map(item => ({
        hours: item.hours || 0,
        hourlyRateId: item.hourlyRateId || '',
        description: item.description || ''
      }));
    }
    
    if (materials.length === 0) {
      materials = [{ description: '', amount: 0, quantity: 1 }];
    }
    if (labor.length === 0) {
      labor = [{ hours: 0, hourlyRateId: '', description: '' }];
    }
    
    setFormData({
      ...template,
      materials,
      labor
    });
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm('Standardauftrag wirklich löschen?')) {
      try {
        await api.delete(`/service-templates/${id}`, tenantId);
        loadTemplates();
      } catch (error) {
        console.error('Error deleting template:', error);
      }
    }
  };

  return (
    <div className="service-templates">
      <div className="page-header">
        <h1>Standardaufträge</h1>
        <button type="button" onClick={() => navigate('/service-templates/new')} className="btn-primary">
          Neuer Standardauftrag
        </button>
      </div>

      {showForm && (
        <div className="modal">
          <div className="modal-content">
            <h2>{editingTemplate ? 'Standardauftrag bearbeiten' : 'Neuer Standardauftrag'}</h2>
            <form onSubmit={handleSubmit}>
              <div className="form-group">
                <label>Name</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                />
              </div>
              <div className="form-group">
                <label>Materialien</label>
                {formData.materials.map((material, index) => (
                  <div key={index} className="item-row">
                    <input
                      type="text"
                      placeholder="Beschreibung"
                      value={material.description}
                      onChange={(e) => handleMaterialChange(index, 'description', e.target.value)}
                      className="item-description"
                    />
                    <div className="item-quantity-wrapper">
                      <input
                        type="number"
                        placeholder="Menge"
                        value={material.quantity}
                        onChange={(e) => handleMaterialChange(index, 'quantity', e.target.value)}
                        onBlur={() => handleMaterialBlur(index, 'quantity')}
                        className="item-quantity"
                        min="0"
                        step="0.01"
                      />
                      <span className="input-unit">Stück</span>
                    </div>
                    <div className="item-amount-wrapper">
                      <input
                        type="number"
                        placeholder="Betrag"
                        value={material.amount}
                        onChange={(e) => handleMaterialChange(index, 'amount', e.target.value)}
                        onBlur={() => handleMaterialBlur(index, 'amount')}
                        className="item-amount"
                        min="0"
                        step="0.01"
                      />
                      <span className="input-unit">{currency}</span>
                    </div>
                    <button
                      type="button"
                      onClick={() => removeMaterial(index)}
                      className="btn-small btn-danger"
                    >
                      Entfernen
                    </button>
                  </div>
                ))}
                <button type="button" onClick={addMaterial} className="btn-secondary btn-small">
                  Material hinzufügen
                </button>
              </div>

              <div className="form-group">
                <label>Arbeitseinsatz</label>
                {formData.labor.map((lab, index) => (
                  <div key={index} className="item-row-extended">
                    <div className="item-hours-wrapper">
                      <input
                        type="number"
                        placeholder="Stunden"
                        value={lab.hours}
                        onChange={(e) => handleLaborChange(index, 'hours', e.target.value)}
                        onBlur={() => handleLaborBlur(index, 'hours')}
                        className="item-hours"
                        min="0"
                        step="0.1"
                      />
                      <span className="input-unit">h</span>
                    </div>
                    <select
                      value={lab.hourlyRateId || ''}
                      onChange={(e) => handleLaborChange(index, 'hourlyRateId', e.target.value)}
                      className="item-hourly-rate"
                      style={{ flex: 1 }}
                    >
                      <option value="">Stundensatz wählen</option>
                      {hourlyRates.map(rate => (
                        <option key={rate.id} value={rate.id}>
                          {rate.name} ({rate.rate.toFixed(2)} {currency}/h)
                        </option>
                      ))}
                    </select>
                    {lab.hours && lab.hourlyRateId && (
                      <div className="item-amount-wrapper">
                        <span className="input-unit" style={{ position: 'static', padding: '0.5rem' }}>
                          {calculateLaborAmount(lab).toFixed(2)} {currency}
                        </span>
                      </div>
                    )}
                    <button
                      type="button"
                      onClick={() => removeLabor(index)}
                      className="btn-small btn-danger"
                    >
                      Entfernen
                    </button>
                  </div>
                ))}
                <button type="button" onClick={addLabor} className="btn-secondary btn-small">
                  Arbeitseinsatz hinzufügen
                </button>
              </div>

              <div className="form-actions">
                <button type="submit" className="btn-primary">
                  {editingTemplate ? 'Speichern' : 'Erstellen'}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setShowForm(false);
                    setEditingTemplate(null);
                  }}
                  className="btn-secondary"
                >
                  Abbrechen
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      <DataTable
        data={templates}
        columns={[
          {
            key: 'actions',
            header: '',
            sortable: false,
            width: '80px',
            render: (row) => (
              <div style={{ display: 'inline-flex', gap: '0.25rem', whiteSpace: 'nowrap' }}>
                <IconButton
                  icon="edit"
                  onClick={() => handleEdit(row)}
                  title="Bearbeiten"
                />
                <IconButton
                  icon="delete"
                  onClick={() => handleDelete(row.id)}
                  title="Löschen"
                  className="danger"
                />
              </div>
            )
          },
          {
            key: 'name',
            header: 'Name',
            sortType: 'string',
            accessor: (row) => row.name || '',
            render: (row) => (
              <button 
                className="link-button" 
                onClick={() => navigate(`/service-templates/${row.id}`)}
              >
                {row.name || ''}
              </button>
            )
          },
          {
            key: 'materials',
            header: 'Materialien',
            sortable: false,
            render: (row) => {
              const materials = row.materials || [];
              const materialsAmount = materials.reduce((sum, mat) => sum + ((mat.amount || 0) * (mat.quantity || 1)), 0);
              return `${materials.length} Materialien (${materialsAmount.toFixed(2)} ${currency})`;
            }
          },
          {
            key: 'labor',
            header: 'Arbeitseinsatz',
            sortable: false,
            render: (row) => {
              const labor = row.labor || [];
              const totalHours = labor.reduce((sum, lab) => sum + (lab.hours || 0), 0);
              return `${labor.length} Positionen (${totalHours.toFixed(1)}h)`;
            }
          }
        ]}
        defaultSort={{ key: 'name', direction: 'asc' }}
        emptyMessage="Keine Standardaufträge vorhanden"
      />
    </div>
  );
}

export default ServiceTemplates;
